import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import type { DeckCard, Intent, Tier } from "./types";

// Thin, TYPED wrappers over the same callable Cloud Functions the native apps
// use. Request shapes are verified against firebase/functions/src — parameter
// names must match exactly (the server destructures them).
const call = <Req, Res>(name: string) => {
  const fn = httpsCallable<Req, Res>(functions, name);
  return async (data?: Req): Promise<Res> => (await fn(data as Req)).data;
};

// ---- Discover / matching ----
export const fetchDeck = call<void, { deck: DeckCard[] }>("fetchDeck");
/** Server expects `liked` (functions/src/matching.ts). */
export const swipe = call<{ targetUid: string; liked: boolean }, { matched: boolean; matchId: string | null }>("swipe");

// ---- Messaging (allotment enforced server-side) ----
export const sendMessage = call<{ matchId: string; text: string }, { exhausted: boolean }>("sendMessage");
export const markRead = call<{ matchId: string }, { ok: boolean }>("markRead");
export const unmatch = call<{ matchId: string }, { ok: boolean }>("unmatch");

// ---- Paywall ----
export const submitPaywallIntent = call<
  { matchId: string; intent: Exclude<Intent, null> },
  { status: "expired" | "waitingForOther" | "resolved" | "unlocked"; tier?: Tier; price?: string; downgraded?: string[] }
>("submitPaywallIntent");
export const applyPaywallCredit = call<
  { matchId: string },
  { status: "unlocked" | "paidWaitingForOther"; tier: Tier }
>("applyPaywallCredit");
export const createStripeCheckout = call<{ matchId: string }, { url: string }>("createStripeCheckout");

// ---- Relay (group rooms) ----
export const joinFishbowl = call<void, { status: string }>("joinFishbowl");
export const leaveFishbowl = call<void, { status: string }>("leaveFishbowl");
export const leaveActiveFishbowl = call<void, { status: string }>("leaveActiveFishbowl");
export const sendFishbowlMessage = call<{ fishbowlId: string; text: string }, { ok: boolean }>("sendFishbowlMessage");
export const submitTeamAnswer = call<{ fishbowlId: string; questionIndex: number; answer: string }, { ok: boolean }>("submitTeamAnswer");
export const sendRelayPairMessage = call<{ fishbowlId: string; text: string }, { ok: boolean }>("sendRelayPairMessage");

// Headbandz (co-op word game) — server expects `text` for clues AND guesses.
export const getHeadbandzWord = call<{ fishbowlId: string }, { word: string | null; next?: string | null }>("getHeadbandzWord");
export const submitClue = call<{ fishbowlId: string; text: string }, { ok: boolean }>("submitClue");
export const submitGuess = call<{ fishbowlId: string; text: string }, { correct: boolean }>("submitGuess");
export const passWord = call<{ fishbowlId: string }, { ok: boolean }>("passWord");

// Funny Sentences (acro) — server expects `targetId` for votes.
export const submitSentence = call<{ fishbowlId: string; round: number; sentence: string }, { ok: boolean }>("submitSentence");
export const submitVote = call<{ fishbowlId: string; round: number; targetId: string }, { ok: boolean }>("submitVote");
export const submitLaugh = call<{ fishbowlId: string; round: number; targetId: string }, { ok: boolean }>("submitLaugh");

// ---- Duel (1v1 Relay) — contracts from functions/src/duel.ts ----
export const joinDuel = call<void, { status: string }>("joinDuel");
export const leaveDuel = call<void, { status: string }>("leaveDuel");
export const leaveDuelLobby = call<{ duelId: string }, { ok: boolean }>("leaveDuelLobby");
export const resolveNoShowDuel = call<{ duelId: string }, { cancelled: boolean; reason?: string }>("resolveNoShowDuel");
export const submitDuelAnswer = call<{ duelId: string; questionIndex: number; answer: string }, { ok: boolean }>("submitDuelAnswer");
export const chooseDuelMatch = call<{ duelId: string; want: boolean }, { status: "matched" | "passed" | "waiting"; matchId?: string }>("chooseDuelMatch");
export const getDuelWord = call<{ duelId: string }, { word: string | null; next?: string | null }>("getDuelWord");
export const submitDuelClue = call<{ duelId: string; text: string }, { ok: boolean }>("submitDuelClue");
export const submitDuelGuess = call<{ duelId: string; text: string }, { correct: boolean }>("submitDuelGuess");
export const passDuelWord = call<{ duelId: string }, { ok: boolean }>("passDuelWord");
export const submitDuelSentence = call<{ duelId: string; round: number; sentence: string }, { ok: boolean; wonRound?: boolean }>("submitDuelSentence");
export const reactDuelSentence = call<{ duelId: string; round: number; emoji: string }, { ok: boolean }>("reactDuelSentence");

// ---- Safety (server expects `targetUid` — functions/src/safety.ts) ----
export const blockUser = call<{ targetUid: string }, { ok: boolean }>("blockUser");
export const reportUser = call<{ targetUid: string; reason: string; details?: string }, { ok: boolean }>("reportUser");
export const deleteAccount = call<void, { ok: boolean }>("deleteAccount");
